// Single source of truth for all page copy.
// Every factual claim below is drawn from the HR Anexi Employee Engagement factsheet.

export const ASSETS = {
  logo: "https://customer-assets-lqy194kg.emergentagent.net/job_ef884c6b-b971-492c-8682-58685fa3772f/artifacts/8mczmwd2_HRA%20Logo-%20Hi%20Res.png",
  xModel: "https://customer-assets-lqy194kg.emergentagent.net/job_ef884c6b-b971-492c-8682-58685fa3772f/artifacts/egy5t3aa_X-model.png",
  xModelPhoto: "https://customer-assets-0z36b82j.emergentagent.net/job_anexi-diagnostic/artifacts/okvzh3lx_Untitled%20design.png",
  footerGraphic: "https://customer-assets-0z36b82j.emergentagent.net/job_anexi-diagnostic/artifacts/7dujw5g0_2.png",
  banner: "https://customer-assets-0z36b82j.emergentagent.net/job_anexi-diagnostic/artifacts/283p9ggh_1.png",
  bannerAlt:
    "HR Anexi — Beyond the Engagement Scores: Leadership Priorities. Identify what's driving engagement, understand differences across segments, benchmark with relevant peers, see experiences across functions and locations, focus on what matters most.",
};

export const CONTACT = {
  email: "sales@hranexi.com",
  website: "www.hranexi.com",
  websiteUrl: "https://www.hranexi.com",
  offices: [
    { city: "Bengaluru", phone: "+91 9590044928", tel: "+919590044928" },
    { city: "Mumbai", phone: "+91 22 67401000", tel: "+912267401000" },
  ],
  tagline: "Powering Organizations. Empowering People.",
};

export const NAV_ITEMS = [
  { id: "overview", label: "Overview" },
  { id: "model", label: "The Model" },
  { id: "outcomes", label: "Results" },
  { id: "contact", label: "Contact" },
];

export const HERO = {
  eyebrow: "Employee Engagement Survey",
  headlineA: "What does engagement mean?",
  headlineB: "And what exactly does an engaged employee look like?",
  support:
    "There are many different conceptual frameworks used to describe engagement. You've probably heard a lot about performance, discretionary effort, ownership, culture, leadership, etc., the list goes on and on. A good number of these things are indeed very important.",
  supportEmphasis:
    "But before jumping into the concepts and numbers, first think about what ",
  supportEmphasisHighlight: "EMPLOYEE ENGAGEMENT",
  supportEmphasisTail: " feels like.",
  primaryCta: "Explore the Engagement Survey",
  secondaryCta: "Talk to an HR Anexi Consultant",
  microcopy: "Igniting Employee Spirit",
};

export const QUESTIONS = [
  "How do employees feel about their job? Do they know WIIFM (What's In It For Me)?",
  "Are Engagers Engaged themselves?",
  "Does the manager play a meaningful role in this?",
  "Who wishes to stay or leave? Why or why not?",
  "What drives people performance and happiness?",
  "What do employees feel about the organization and their senior leaders?",
];

export const SEGMENTS = [
  {
    id: "engaged",
    name: "The Engaged",
    contribution: "High",
    satisfaction: "High",
    levelLine: "High contribution & high satisfaction",
    summary:
      "At the apex, where personal and organizational interests align. They contribute fully to the organization's success and find great satisfaction in their work.",
    detail:
      "Known for discretionary effort and commitment. Organizations need to keep them engaged: over time they can transition to any of the three adjacent segments, a move that would likely impact workforce morale and the bottom line.",
    color: "#8F7322",
    hover: "#B08D25",
    ink: "#FFFFFF",
    tone: "gold",
  },
  {
    id: "almost-engaged",
    name: "Almost Engaged",
    contribution: "Medium to high",
    satisfaction: "Medium to high",
    levelLine: "Medium to high contribution & satisfaction",
    summary:
      "Among the high performers, and reasonably satisfied with their job.",
    detail:
      "They may not have consistent \"great days at work\" — but they know what those days look like.",
    color: "#C9A85C",
    hover: "#DBBE78",
    ink: "#1A2734",
    tone: "amber",
  },
  {
    id: "honeymooners-hamsters",
    name: "Honeymooners & Hamsters",
    contribution: "Low",
    satisfaction: "Medium to high",
    levelLine: "Medium to high satisfaction but low contribution",
    summary:
      "Honeymooners are new to the organization or their role — and happy to be there. They have yet to find their stride or clearly understand how they can best contribute.",
    detail:
      "Hamsters may be working hard but are in effect \"spinning their wheels\" — working on non-essential tasks and contributing little to organizational success. Some may be content simply holding their position (\"retired in place\").",
    color: "#2C3E50",
    hover: "#3F5772",
    ink: "#FFFFFF",
    tone: "navy",
  },
  {
    id: "crash-burners",
    name: "Crash & Burners",
    contribution: "Medium to high",
    satisfaction: "Low",
    levelLine: "Medium to high contribution but low satisfaction",
    summary:
      "Disillusioned and potentially exhausted. These are top producers who aren't achieving their personal definition of success and satisfaction.",
    detail:
      "They can be bitterly vocal that senior leaders are making bad decisions or that colleagues are not pulling their weight. Left alone, they are likely to slip down the contribution scale.",
    color: "#2E9BD6",
    hover: "#4FB1E8",
    ink: "#FFFFFF",
    tone: "blue",
  },
  {
    id: "disengaged",
    name: "The Disengaged",
    contribution: "Low to medium",
    satisfaction: "Low to medium",
    levelLine: "Low to medium contribution and satisfaction",
    summary:
      "Most Disengaged employees didn't start out as bad apples — and they still may not be. They are the most disconnected from organizational priorities.",
    detail:
      "They often feel underutilized and are clearly not getting what they need from work. They are likely to be skeptical and can indulge in contagious negativity.",
    color: "#E8621F",
    hover: "#F27A3C",
    ink: "#FFFFFF",
    tone: "orange",
  },
];

export const MODEL = {
  headline: "Our Methodology",
  paragraphs: [
    "Employee Engagement (EE) has been hailed as the secret ingredient to competitive advantage and organizational success. High employee engagement is a key objective for many organizations.",
    "By examining the factors that impact engagement and the role of each member in the organization — from executive to individual contributor — we determine what actions and behaviors differentiate highly engaged organizations from the rest.",
    "Full engagement represents an alignment of maximum job satisfaction (\"I like my work and do it well\") with maximum job contribution (\"I help achieve the goals of my organization\"). The index we use to determine engagement levels contains items that reflect the two axes of contribution and satisfaction.",
  ],
  segmentsIntro: "By plotting a given population against the two axes, we identify 5 distinct employee segments:",
  satisfaction: {
    label: "Satisfaction",
    quote: "I like my work and do it well.",
    description: "Personal satisfaction in the role.",
  },
  contribution: {
    label: "Contribution",
    quote: "I help achieve the goals of my organization.",
    description: "Contribution to the company's success.",
  },
  apex: "Full Engagement",
};

export const MATRIX = {
  note: "Select the engagement state to know more.",
};

export const EES_INTRO =
  "Employee Engagement Survey (EES) assesses employees on parameters like; strategy alignment, trust in leadership and employee-manager dynamics; as well as maps employee on job satisfaction and contribution matrix. It not only provides a quick check of engagement levels within the organization but also delivers concise, actionable data analysis which can be translated in seamless developmental initiatives for employees thus creating an overall positive impact driving organizational success.";

export const OUTCOMES = {
  heading: "Results",
  items: [
    "A clear picture of who is engaged — and who is not",
    "Assessment of your leadership's impact on workforce engagement",
    "Identification of the factors impacting your organization's performance and employee engagement",
    "Substantial data to help plan and measure strategic workforce development initiatives",
    "Personalized analysis and recommendations in the report to Management / Board",
  ],
  deliverables: [
    "Company-specific customized Engagement Report",
    "Customized Recommendations – Action Planning",
  ],
  surveyOutcomesHeading: "Survey Outcomes:",
  surveyOutcomesList: [
    "Company Specific Customized Engagement Report",
    "Organization with best Employee Engagement practices",
  ],
  cultureParagraphs: [
    "Surveys don't change organizational culture, people taking action do. While the survey is a useful diagnostic tool, providing interesting insights into what is happening in your organization, assessment is not enough. HR Anexi views the survey not as the end product of engagement but really the starting point in a dialogue with your employees on how to sustain and maintain engagement.",
    "Organizational commitment and implementing actions to address the survey results are paramount to creating a truly engaged workforce.",
  ],
};

export const DIFFERENTIATORS = {
  heading: "Survey Design & Differentiating factors",
  items: [
    "Short, focused online + offline survey",
    "Multi-lingual - 33+ languages",
    "Customizable questionnaire based on your organization needs & focus areas",
    "50+ years of research. Global and Indian industry benchmarks",
    "AI enabled chatbot and real time dashboards for enhanced user/administrator experience",
  ],
};

export const FINAL_CTA = {
  headlineMain: "Beyond the engagement score",
  headlineSub: "why should anyone work here?",
  formTitle: "Request a call back from an engagement expert",
};
